import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import { addUserAddress } from '../../../features/addressSlice';
import AdminCityManager from '../../../services/home/CityManager';

const modalBackdrop = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};
const modalContent = {
  hidden: { y: 50, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

export default function AddAddressModal({
  isOpen,
  onClose,
  userId,
  initialData = null,
  onSuccess,
}) {
  const dispatch = useDispatch();

  // form state
  const [fullAddress, setFullAddress] = useState(initialData?.fullAddress || '');
  const [cityId, setCityId]           = useState(initialData?.cityId?.toString() || '');
  const [pincodeId, setPincodeId]     = useState(initialData?.pincodeId?.toString() || '');
  const [submitting, setSubmitting]   = useState(false);

  // fetched active cities + pincodes
  const [cities, setCities] = useState([]);

  // load active cities once modal opens
  useEffect(() => {
    if (!isOpen || !initialData) return;

    setFullAddress(initialData.fullAddress || '');

    const fallbackCityId = initialData.cityId || initialData.city?.cityId;
    const fallbackPinId  = initialData.pincodeId || initialData.pincode?.pinId;

    setCityId(fallbackCityId?.toString() || '');
    setPincodeId(fallbackPinId?.toString() || '');
  }, [isOpen, initialData]);


  useEffect(() => {
    if (!initialData || cities.length === 0) return;

    const matchCity = cities.find((c) => c.cityId === initialData.cityId);
    if (matchCity) {
      setCityId(initialData.cityId.toString());

      const hasPin = matchCity.pincodes?.some(
        (p) => p.pinId === initialData.pincodeId
      );
      if (hasPin) {
        setPincodeId(initialData.pincodeId.toString());
      }
    }
  }, [initialData, cities]);

  useEffect(() => {
    // Match 6-digit pincode using regex
    const match = fullAddress.match(/\b\d{6}\b/);
    if (!match) return;

    const typedPin = match[0];

    for (const city of cities) {
      const pin = city.pincodes.find(p => p.pinCode === typedPin);
      if (pin) {
        setCityId(city.cityId.toString());
        setPincodeId(pin.pinId.toString());
        break;
      }
    }
  }, [fullAddress, cities]);

  useEffect(() => {
    if (!isOpen) return;

    AdminCityManager.getActiveCitiesWithPincodes()
      .then(setCities)
      .catch(() => toast.error('Failed to load cities'));
  }, [isOpen]);



  // when you pick a city, reset pincode selection
  const handleCityChange = (e) => {
    const newCityId = e.target.value;
    setCityId(newCityId);
    setPincodeId('');

    const selected = cities.find((c) => c.cityId?.toString() === newCityId);
    if (selected) {
      const state = selected.state || '';
      const cityStateText = `${selected.cityName}, ${state}`.trim();

      // Remove any old city/state if already appended
      setFullAddress((prev) => {
        // Remove any existing ", City, STATE" pattern
        const cleaned = prev.replace(/,\s*\w+,\s*[A-Z]+$/i, '').trim();
        return cleaned ? `${cleaned}, ${cityStateText}` : cityStateText;
      });
    }
  };

  // derive only that city’s pincodes
  const selectedCity = cities.find(c => c.cityId?.toString() === cityId);
  const pincodes     = selectedCity?.pincodes || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullAddress || !cityId || !pincodeId) {
      toast.error('Please fill in all fields.');
      return;
    }
    const payload = {
      fullAddress,
      cityId:    Number(cityId),
      pincodeId: Number(pincodeId),
    };

    try {
      setSubmitting(true);
      await dispatch(addUserAddress({ userId, payload })).unwrap();
      toast.success('Address saved!');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to save address');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          variants={modalBackdrop}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          <motion.div
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg mx-4 relative"
            variants={modalContent}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            <button
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
              onClick={onClose}
            >
              <X size={20} />
            </button>
            <h2 className="text-2xl font-bold mb-4 text-gray-800">
              Add Delivery Address
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Full Address */}
              <div>
                <label className="block text-gray-700 mb-1">Full Address</label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  rows={3}
                  value={fullAddress}
                  onChange={(e) => setFullAddress(e.target.value)}
                  placeholder="123 Baker Street"
                />
              </div>

              {/* City & Pincode */}
              <div className="grid grid-cols-2 gap-4">
                {/* City */}
                <div>
                  <label className="block text-gray-700 mb-1">City</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    value={cityId}
                    onChange={handleCityChange}
                  >
                    <option value="">City</option>
                    {cities.map(({ cityId, cityName }) => (
                      <option key={cityId} value={cityId}>
                        {cityName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Pin Code */}
                <div>
                  <label className="block text-gray-700 mb-1">Pin Code</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    value={pincodeId}
                    onChange={(e) => {
                      const newPinId = e.target.value;
                      setPincodeId(newPinId);
                      const selectedPin = pincodes.find(p => p.pinId.toString() === newPinId);
                      if (selectedPin) {
                        setFullAddress(prev =>
                          prev.replace(/\b\d{6}\b/g, '').trim() + ` ${selectedPin.pinCode}`
                        );
                      }
                    }}
                    disabled={!cityId}
                  >
                    <option value="">Select Pin Code</option>
                    {pincodes.map(({ pinId, pinCode }) => (
                      <option key={pinId} value={pinId}>
                        {pinCode}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary text-white py-2 rounded-lg font-semibold hover:bg-primary-shade5 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Save Address'}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
